import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";
import "./Catalog.css";
import Footer from "./Footer";
import { useProductState, useProductActions } from "../contexts/ProductContext";
import Header from "./Header";
// Modular components
import CatalogFilters from "./CatalogFilters";
import ProductsGrid from "./ProductsGrid";
import ProductDrawer from "./ProductDrawer";
import {
  CatalogSkeleton,
  CatalogLoadMoreSkeleton,
  DrawerSkeleton,
} from "./Skeleton";
import {
  mergeProductsWithSpecs,
  applyFiltersAndSort,
  normalizeString,
} from "./catalogUtils";
import segmentData from "../utils/segmentData";
import {
  getCachedCounts,
  setCachedCounts,
  getCachedProducts,
  setCachedProducts,
  pushNavigationState,
} from "../utils/segmentCache";

const STORAGE_KEY = "lastSelectedSegment";
const PAGE_SIZE = 12;

const Catalog = () => {
  const { segment, purity, category } = useParams();
  console.log("=== Catalog useParams ===", { segment, purity, category });
  const [resolvedParams, setResolvedParams] = useState({
    segment,
    purity,
    category,
  });

  // Initialize filter/sort state from localStorage or defaults
  const storedFilters = localStorage.getItem("catalogFilters");
  const storedSort = localStorage.getItem("catalogSort");

  const [filters, setFilters] = useState(() => {
    if (storedFilters) {
      try {
        return JSON.parse(storedFilters);
      } catch (e) {
        console.warn("Failed to parse stored filters:", e);
      }
    }
    return { serviceType: "ready", productSource: "" };
  });

  const [sortConfig, setSortConfig] = useState(() => {
    if (storedSort) {
      try {
        return JSON.parse(storedSort);
      } catch (e) {
        console.warn("Failed to parse stored sort:", e);
      }
    }
    return { field: "", direction: "" };
  });

  // State to hold counts for service types and product sources
  const [filterCounts, setFilterCounts] = useState({
    ready: 0,
    order: 0,
    sources: {},
  });

  useEffect(() => {
    if (segment && purity && category) {
      setResolvedParams({ segment, purity, category });
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ segment, purity, category }),
      );
    } else {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed.segment && parsed.purity && parsed.category) {
            setResolvedParams(parsed);
          }
        } catch (e) {
          console.warn("Failed to parse stored segment:", e);
        }
      }
    }
  }, [segment, purity, category]);

  const effectiveSegment = resolvedParams.segment || segment;
  const effectivePurity = resolvedParams.purity || purity;
  const effectiveCategory = resolvedParams.category || category;

  const decodedSegment = decodeURIComponent(effectiveSegment || "").trim();
  const decodedSegmentUpper = decodedSegment.toUpperCase();
  const decodedPurity = decodeURIComponent(effectivePurity || "").trim();
  const decodedCategory = decodeURIComponent(effectiveCategory || "").trim();

  console.log("=== Catalog decoded values ===", {
    decodedSegmentUpper,
    decodedPurity,
    decodedCategory,
  });

  const findKey = (obj, lookupValue) => {
    if (!obj || typeof obj !== "object") return null;
    const keys = Object.keys(obj);

    // Exact match
    const exact = keys.find((k) => k === lookupValue);
    if (exact) return exact;

    // Case-insensitive match
    const upper = lookupValue.toUpperCase();
    const ci = keys.find((k) => k.toUpperCase() === upper);
    if (ci) return ci;

    // Normalized match: replace hyphens/spaces, trim, compare
    const normalize = (str) =>
      str
        .toString()
        .trim()
        .replace(/[-_\s]+/g, " ")
        .replace(/\s+/g, " ")
        .toUpperCase();
    const nl = keys.find((k) => normalize(k) === normalize(lookupValue));
    if (nl) return nl;

    return null;
  };

  const matchedSegmentKey = findKey(segmentData, decodedSegmentUpper);
  const matchedPurityKey = matchedSegmentKey
    ? findKey(segmentData[matchedSegmentKey], decodedPurity)
    : null;
  const matchedCategoryKey =
    matchedPurityKey && matchedSegmentKey
      ? findKey(
          segmentData[matchedSegmentKey][matchedPurityKey],
          decodedCategory,
        )
      : null;

  // Firebase query values (uppercase to match Firebase data)
  const fbSegment = matchedSegmentKey || decodedSegmentUpper;
  const fbPurity = matchedPurityKey
    ? matchedPurityKey.toUpperCase()
    : decodedPurity.toUpperCase();
  const fbCategory = matchedCategoryKey
    ? matchedCategoryKey.toUpperCase()
    : decodedCategory.toUpperCase();

  const productSources = useMemo(() => {
    const sources = matchedCategoryKey
      ? segmentData[matchedSegmentKey][matchedPurityKey][matchedCategoryKey]
          ?.productSources || []
      : [];

    console.log("=== Catalog productSources lookup ===", {
      decodedSegmentUpper,
      decodedPurity,
      decodedCategory,
      matchedSegmentKey,
      matchedPurityKey,
      matchedCategoryKey,
      fbSegment,
      fbPurity,
      fbCategory,
      hasSegment: !!matchedSegmentKey,
      hasPurity: !!matchedPurityKey,
      hasCategory: !!matchedCategoryKey,
      sourcesCount: sources.length,
      sources,
    });
    return sources;
  }, [
    matchedSegmentKey,
    matchedPurityKey,
    matchedCategoryKey,
    decodedSegmentUpper,
    decodedPurity,
    decodedCategory,
    fbSegment,
    fbPurity,
    fbCategory,
  ]);

  const { likedProducts, selectedProducts } = useProductState();
  const { toggleLike, addToSelected } = useProductActions();

  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError] = useState(null);

  const productCursorRef = useRef(null);
  const productsFetchingRef = useRef(false);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [sizes, setSizes] = useState([{ size: "Regular", quantity: 1 }]);

  const startX = useRef(0);
  const isDragging = useRef(false);
  const filterBarRef = useRef(null);

  // ======================
  // LOCAL COUNT AGGREGATION (with memory cache)
  // ======================
  const fetchCounts = useCallback(async () => {
    if (!matchedSegmentKey || !matchedPurityKey || !matchedCategoryKey) {
      return;
    }

    // Check in-memory cache first
    const cached = getCachedCounts(
      matchedSegmentKey,
      matchedPurityKey,
      matchedCategoryKey,
      filters.serviceType,
    );
    if (cached) {
      setFilterCounts(cached);
      return;
    }

    // Check localStorage for cached counts
    const storedCountsKey = `${matchedSegmentKey}_${matchedPurityKey}_${matchedCategoryKey}_${filters.serviceType}`;
    const stored = localStorage.getItem(storedCountsKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const age = Date.now() - parsed.timestamp;
        if (age < 15 * 60 * 1000) {
          setFilterCounts(parsed.counts);
          setCachedCounts(
            matchedSegmentKey,
            matchedPurityKey,
            matchedCategoryKey,
            filters.serviceType,
            parsed.counts,
          );
          return;
        }
      } catch (e) {}
    }

    try {
      // Fetch products to count locally (more efficient than COUNT queries for small datasets)
      const productsRef = collection(db, "products");
      const readyQ = query(
        productsRef,
        where("segment", "==", matchedSegmentKey),
        where("category", "==", matchedPurityKey),
        where("productName", "==", matchedCategoryKey),
        where("status", "==", "approved"),
        where("serviceType", "==", "ready"),
      );
      const orderQ = query(
        productsRef,
        where("segment", "==", matchedSegmentKey),
        where("category", "==", matchedPurityKey),
        where("productName", "==", matchedCategoryKey),
        where("status", "==", "approved"),
        where("serviceType", "==", "order"),
      );

      const [readySnap, orderSnap] = await Promise.all([
        getDocs(readyQ),
        getDocs(orderQ),
      ]);

      const readyCount = readySnap.size;
      const orderCount = orderSnap.size;

      const currentService = filters.serviceType || "ready";
      const sourceCounts = {};
      const sourcePromises = productSources.map(async (src) => {
        // Normalize source for case-insensitive matching with Firebase
        const normalizedSource = normalizeString(src);
        const srcQ = query(
          productsRef,
          where("segment", "==", matchedSegmentKey),
          where("category", "==", matchedPurityKey),
          where("productName", "==", matchedCategoryKey),
          where("status", "==", "approved"),
          where("serviceType", "==", currentService),
          where("productSource", "==", normalizedSource),
        );
        const snap = await getDocs(srcQ);
        return { source: src, count: snap.size };
      });

      const sourceResults = await Promise.all(sourcePromises);
      sourceResults.forEach(({ source, count }) => {
        sourceCounts[source] = count;
      });

      const counts = {
        ready: readyCount,
        order: orderCount,
        sources: sourceCounts,
      };

      setFilterCounts(counts);
      setCachedCounts(
        matchedSegmentKey,
        matchedPurityKey,
        matchedCategoryKey,
        filters.serviceType,
        counts,
      );

      // Cache in localStorage
      localStorage.setItem(
        storedCountsKey,
        JSON.stringify({ counts, timestamp: Date.now() }),
      );
    } catch (err) {
      console.error("❌ Error fetching filter counts:", err);
    }
  }, [
    matchedSegmentKey,
    matchedPurityKey,
    matchedCategoryKey,
    filters.serviceType,
    productSources,
  ]);
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // ======================
  // PAGINATED FETCH PRODUCTS (with memory cache)
  // ======================
  const fetchProductsPage = useCallback(
    async (reset = false) => {
      if (productsFetchingRef.current) return;
      if (!reset && !hasMoreProducts) return;
      if (!matchedSegmentKey || !matchedPurityKey || !matchedCategoryKey)
        return;

      // Check in-memory cache for full product list
      if (!reset && allProducts.length > 0) {
        const cached = getCachedProducts(
          matchedSegmentKey,
          matchedPurityKey,
          matchedCategoryKey,
          filters.serviceType,
        );
        if (
          cached &&
          cached.products &&
          cached.products.length >= allProducts.length
        ) {
          setAllProducts(cached.products);
          setHasMoreProducts(cached.hasMore);
          setFilteredProducts(cached.filteredProducts);
          productCursorRef.current = cached.cursor;
          return;
        }
      }

      productsFetchingRef.current = true;
      setProductsLoading(true);
      try {
        if (reset) {
          productCursorRef.current = null;
          setAllProducts([]);
          setHasMoreProducts(true);
        }

        let q = query(
          collection(db, "products"),
          where("segment", "==", matchedSegmentKey),
          where("category", "==", matchedPurityKey),
          where("productName", "==", matchedCategoryKey),
          where("status", "==", "approved"),
          where("serviceType", "==", filters.serviceType || "ready"),
          orderBy("__name__"),
          limit(PAGE_SIZE),
        );
        if (productCursorRef.current) {
          q = query(q, startAfter(productCursorRef.current));
        }

        const snap = await getDocs(q);
        const docs = snap.docs;

        const matched = docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (p) =>
              p.segment === matchedSegmentKey &&
              p.category === matchedPurityKey &&
              p.productName === matchedCategoryKey &&
              p.status === "approved" &&
              p.serviceType !== "out",
          );

        productCursorRef.current = docs.length
          ? docs[docs.length - 1]
          : productCursorRef.current;
        const newHasMore = docs.length === PAGE_SIZE;
        setHasMoreProducts(newHasMore);

        // Update state with new products
        setAllProducts((prev) => {
          const combined = reset ? matched : [...prev, ...matched];
          setCachedProducts(
            matchedSegmentKey,
            matchedPurityKey,
            matchedCategoryKey,
            filters.serviceType,
            {
              products: combined,
              hasMore: newHasMore,
              filteredProducts: filteredProducts,
              cursor: productCursorRef.current,
            },
          );
          return combined;
        });
      } catch (err) {
        console.error("❌ Error fetching products:", err);
        setError(`Error loading products: ${err.message}`);
      } finally {
        productsFetchingRef.current = false;
        setProductsLoading(false);
      }
    },
    [
      matchedSegmentKey,
      matchedPurityKey,
      matchedCategoryKey,
      hasMoreProducts,
      allProducts,
      filteredProducts,
      filters.serviceType,
    ],
  );

  const fetchSpecForProduct = useCallback(async (product) => {
    if (!product?.productName) return null;
    try {
      const isRudraksha = (p) =>
        p.productName === "rudraksha" ||
        normalizeString(p.productName || "").toLowerCase() === "rudraksha" ||
        normalizeString(p.productName || "").toLowerCase().includes("rudraksha");

      const isRudrakshaVal = isRudraksha(product);
      const productSegment = normalizeString(
        product.segment || "",
      ).toLowerCase();
      const isGemsOrPearls =
        productSegment === "gems" || productSegment === "pearls";
      const isDecor = productSegment === "decor";

      let snapshot;
      if (isRudrakshaVal) {
        snapshot = await getDocs(collection(db, "productSpecs"));
      } else {
        const q = query(
          collection(db, "productSpecs"),
          where("segment", "==", product.segment),
          where("category", "==", product.category),
        );
        snapshot = await getDocs(q);
      }

      const allSpecs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const ns = (v) => normalizeString(v || "");

      // Fallback match order: segment → category → productSource → productName → finishType
      const matchers = [
        (spec) =>
          spec.segment === product.segment &&
          spec.category === product.category &&
          ns(spec.productSource) === ns(product.productSource) &&
          ns(spec.productName) === ns(product.productName) &&
          ns(spec.finishType) === ns(product.finishType),
        (spec) =>
          spec.segment === product.segment &&
          spec.category === product.category &&
          ns(spec.productSource) === ns(product.productSource) &&
          ns(spec.productName) === ns(product.productName),
        (spec) =>
          spec.segment === product.segment &&
          spec.category === product.category &&
          ns(spec.productName) === ns(product.productName) &&
          ns(spec.finishType) === ns(product.finishType),
        (spec) =>
          spec.segment === product.segment &&
          spec.category === product.category &&
          ns(spec.productName) === ns(product.productName),
      ];

      let matchedSpec = null;
      for (const match of matchers) {
        matchedSpec = allSpecs.find(match);
        if (matchedSpec) break;
      }

      // Non-rudraksha segments keep their extra field rules
      if (!isRudrakshaVal && matchedSpec) {
        if (isGemsOrPearls) {
          const ok =
            matchedSpec.color &&
            product.color &&
            ns(matchedSpec.color) === ns(product.color) &&
            matchedSpec.shape &&
            product.shape &&
            ns(matchedSpec.shape) === ns(product.shape);
          if (!ok) matchedSpec = null;
        } else if (isDecor) {
          const ok =
            matchedSpec.color &&
            product.color &&
            ns(matchedSpec.color) === ns(product.color);
          if (!ok) matchedSpec = null;
        } else {
          const ok =
            matchedSpec.specification &&
            product.specification &&
            ns(matchedSpec.specification) === ns(product.specification);
          if (!ok) matchedSpec = null;
        }
      }

      console.log(matchedSpec);
      return matchedSpec || null;
    } catch (err) {
      console.error("❌ Error fetching spec for product:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (productSources.length > 0 && !filters.productSource) {
      setFilters((prev) => ({
        ...prev,
        productSource: productSources[0],
      }));
    }
  }, [productSources, filters.productSource]);

  useLayoutEffect(() => {
    const update = () => {
      const el = filterBarRef.current;
      if (el) {
        document.documentElement.style.setProperty(
          "--catalog-filter-height",
          `${el.offsetHeight}px`,
        );
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [productSources, filters.productSource]);

  useEffect(() => {
    if (effectiveSegment && effectivePurity && effectiveCategory) {
      let mounted = true;
      const init = async () => {
        setLoading(true);
        setError(null);
        await fetchProductsPage(true);
        if (mounted) setLoading(false);
      };
      init();
      return () => {
        mounted = false;
      };
    } else {
      setError("Missing segment parameters. Please select from the dashboard.");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSegment, effectivePurity, effectiveCategory, filters.serviceType]);

  // Push navigation state when segment/purity/category changes
  useEffect(() => {
    if (effectiveSegment && effectivePurity && effectiveCategory) {
      pushNavigationState(effectiveSegment, effectivePurity, effectiveCategory);
    }
  }, [effectiveSegment, effectivePurity, effectiveCategory]);

  useEffect(() => {
    if (allProducts.length === 0) {
      setFilteredProducts([]);
      return;
    }
    const result = applyFiltersAndSort(allProducts, filters, sortConfig);
    setFilteredProducts(result);
  }, [allProducts, filters, sortConfig]);

  const loadMoreRef = useRef(() => {});
  loadMoreRef.current = () => {
    fetchProductsPage(false);
  };

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreRef.current();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, hasMoreProducts]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]:
        filterType === "serviceType"
          ? value
          : value === prev[filterType]
          ? ""
          : value,
    }));
    setSortConfig({ field: "", direction: "" });
  };

  const handleResetFilters = () => {
    setFilters({
      serviceType: "ready",
      productSource: productSources[0] || "",
    });
    setSortConfig({ field: "", direction: "" });
  };

  const handleSortClick = (field) => {
    setSortConfig((prev) =>
      prev.field === field
        ? { ...prev, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "desc" },
    );
  };

  const handleRecommendedClick = () => {
    setSortConfig({ field: "", direction: "" });
  };

  const getCurrentSourceIndex = () =>
    productSources.indexOf(filters.productSource);

  const handleSwipeLeft = () => {
    if (productSources.length <= 1) return;
    const nextIndex = (getCurrentSourceIndex() + 1) % productSources.length;
    handleFilterChange("productSource", productSources[nextIndex]);
  };

  const handleSwipeRight = () => {
    const currentIndex = getCurrentSourceIndex();
    if (currentIndex < 1) return;
    const prevIndex =
      (currentIndex - 1 + productSources.length) % productSources.length;
    handleFilterChange("productSource", productSources[prevIndex]);
  };

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
  };
  const handleTouchEnd = (e) => {
    if (!isDragging.current) return;
    const diff = startX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? handleSwipeLeft() : handleSwipeRight();
    isDragging.current = false;
  };
  const handleMouseDown = (e) => {
    startX.current = e.clientX;
    isDragging.current = true;
  };
  const handleMouseUp = (e) => {
    if (!isDragging.current) return;
    const diff = startX.current - e.clientX;
    if (Math.abs(diff) > 50) diff > 0 ? handleSwipeLeft() : handleSwipeRight();
    isDragging.current = false;
  };
  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  const handleSelectedClick = useCallback(
    async (product) => {
      setSizes([{ size: "Regular", quantity: 1 }]);
      setDrawerLoading(true);
      setSelectedProduct(null);
      setDrawerOpen(true);

      const spec = await fetchSpecForProduct(product);

      const merged = spec
        ? mergeProductsWithSpecs([product], [spec])[0]
        : { ...product, hasSpecification: false, specId: null };

      setSelectedProduct(merged);
      setDrawerLoading(false);
    },
    [fetchSpecForProduct],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedProduct(null);
    setDrawerLoading(false);
    setSizes([{ size: "Regular", quantity: 1 }]);
  };

  const handleAddToSelection = (productWithVariants, serviceType) => {
    if (!productWithVariants) return;
    if (!productWithVariants.hasSpecification) {
      alert(
        "Product specifications are not available. Cannot add to order list.",
      );
      return;
    }
    const finalProduct = {
      ...productWithVariants,
      serviceType: serviceType || "order",
    };
    addToSelected(finalProduct);
    setDrawerOpen(false);
  };

  if (loading) return <CatalogSkeleton count={6} />;
  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <Header
        title={`${effectiveSegment}-${effectivePurity}-${effectiveCategory}`}
      />
      <CatalogFilters
        filters={filters}
        sortConfig={sortConfig}
        counts={filterCounts}
        onFilterChange={handleFilterChange}
        onSortClick={handleSortClick}
        onRecommendedClick={handleRecommendedClick}
        onResetFilters={handleResetFilters}
        productSources={productSources}
      />
      <div className="catalog-container">
        <div
          className="swipeable-grid"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <ProductsGrid
            products={filteredProducts}
            purity={effectivePurity}
            likedProducts={likedProducts}
            selectedProducts={selectedProducts}
            onProductClick={handleSelectedClick}
            onLikeClick={toggleLike}
            onSelectedClick={handleSelectedClick}
            onResetFilters={handleResetFilters}
          />
        </div>

        <div ref={sentinelRef} className="scroll-sentinel">
          {productsLoading && <CatalogLoadMoreSkeleton count={2} />}
          {!productsLoading &&
            !hasMoreProducts &&
            filteredProducts.length > 0 && (
              <div className="end-message">
                You've reached the end of the catalogue
              </div>
            )}
        </div>
      </div>

      {drawerOpen &&
        (drawerLoading || !selectedProduct ? (
          <div className="bottom-drawer-overlay" onClick={closeDrawer}>
            <div className="bottom-drawer" onClick={(e) => e.stopPropagation()}>
              <button
                className="close-btn"
                onClick={closeDrawer}
                aria-label="Close"
                style={{ position: "absolute", top: 12, right: 12 }}
              >
                ×
              </button>
              <DrawerSkeleton />
            </div>
          </div>
        ) : (
          <ProductDrawer
            isOpen={drawerOpen}
            product={selectedProduct}
            sizes={sizes}
            serviceType={filters.serviceType}
            onClose={closeDrawer}
            onSizeChange={setSizes}
            onAddToSelection={handleAddToSelection}
          />
        ))}

      <Footer />
    </>
  );
};

export default Catalog;
